import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X, Zap, Building2 } from "lucide-react";

const tiers = [
  {
    name: "Gratis",
    description: "Para empezar sin compromiso",
    price: "$0",
    period: "/mes",
    note: "Siempre gratis",
    features: [
      { text: "Hasta 3 clientes", included: true },
      { text: "Mediciones completas", included: true },
      { text: "Gráficas básicas", included: true },
      { text: "Vista del cliente", included: true },
      { text: "Consultas con IA", included: false },
      { text: "Coach Feedback IA", included: false },
      { text: "Fotos comparativas", included: false },
    ],
    cta: "Comenzar gratis",
    ctaVariant: "secondary",
  },
  {
    name: "Pro",
    description: "Para nutricionistas activos",
    price: "$1.99",
    period: "/cliente/mes",
    note: "Desde $15.92/mes (8 clientes)",
    badge: "Más popular",
    features: [
      { text: "Clientes ilimitados", included: true },
      { text: "Todo de Gratis", included: true },
      { text: "Consultas con IA", included: true, highlight: true },
      { text: "Coach Feedback IA", included: true, highlight: true },
      { text: "Fotos comparativas", included: true, highlight: true },
      { text: "7 tipos de gráficas", included: true },
      { text: "Tokens IA incluidos", included: true },
    ],
    cta: "Probar 14 días gratis",
    ctaVariant: "primary",
    isPopular: true,
  },
  {
    name: "Enterprise",
    icon: Building2,
    description: "White-label para clínicas",
    price: "Custom",
    period: "",
    note: "Contacta para cotización",
    features: [
      { text: "Todo de Pro", included: true },
      { text: "Tu logo y colores", included: true },
      { text: "Prompts IA personalizados", included: true },
      { text: "Dominio propio", included: true },
      { text: "Acceso API", included: true },
      { text: "Soporte dedicado", included: true },
      { text: "SLA garantizado", included: true },
    ],
    cta: "Contactar ventas",
    ctaVariant: "secondary",
  },
];

const volumeDiscounts = [
  { clients: "8 clientes", price: "$1.99/c" },
  { clients: "15 clientes", price: "$1.79/c" },
  { clients: "50 clientes", price: "$1.29/c" },
  { clients: "100 clientes", price: "$0.99/c" },
];

export const PricingSection = ({ onOpenModal }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="precios"
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0808] to-[#050505]" />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#E05D3A] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Precios
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6"
          >
            Simple. Transparente. <span className="text-gradient">Justo.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto"
          >
            Empieza gratis. Escala cuando quieras. El precio baja con cada cliente que agregas.
          </motion.p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className={`relative rounded-2xl p-6 md:p-8 ${
                tier.isPopular
                  ? "bg-gradient-to-b from-[#E05D3A]/20 to-transparent border-2 border-[#E05D3A]/50"
                  : "bg-white/[0.02] border border-white/10"
              }`}
              data-testid={`pricing-card-${tier.name.toLowerCase()}`}
            >
              {/* Popular badge */}
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#E05D3A] text-white text-xs font-semibold rounded-full">
                    <Zap className="w-3 h-3" />
                    {tier.badge}
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  {tier.icon && <tier.icon className="w-5 h-5 text-zinc-400" />}
                  <h3 className="font-outfit text-xl font-medium text-white">
                    {tier.name}
                  </h3>
                </div>
                <p className="text-zinc-500 text-sm">{tier.description}</p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-outfit font-semibold text-white">
                    {tier.price}
                  </span>
                  <span className="text-zinc-500 text-sm">{tier.period}</span>
                </div>
                <p className="text-zinc-600 text-xs mt-1">{tier.note}</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className={`w-4 h-4 flex-shrink-0 ${
                        feature.highlight ? "text-[#E05D3A]" : "text-zinc-500"
                      }`} />
                    ) : (
                      <X className="w-4 h-4 text-zinc-700 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${
                      feature.included 
                        ? feature.highlight ? "text-white font-medium" : "text-zinc-300"
                        : "text-zinc-600"
                    }`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={onOpenModal}
                className={`w-full py-3 px-6 rounded-full font-medium text-sm transition-all duration-300 ${
                  tier.ctaVariant === "primary"
                    ? "bg-[#E05D3A] text-white hover:bg-[#C85233] shadow-[0_0_20px_rgba(224,93,58,0.3)] hover:shadow-[0_0_30px_rgba(224,93,58,0.5)]"
                    : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
                }`}
                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
              >
                {tier.cta}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Volume Discount */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="p-6 rounded-2xl bg-white/[0.02] border border-white/10"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#E05D3A]/10">
                <TrendingDown className="w-5 h-5 text-[#E05D3A]" />
              </div>
              <span className="text-white font-medium">
                Mientras más clientes, menos pagas
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              {volumeDiscounts.map((discount, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-zinc-500 text-sm">{discount.clients}</span>
                  <span className="text-[#E05D3A] font-semibold text-sm">→</span>
                  <span className="text-white font-medium text-sm">{discount.price}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Import for the icon
import { TrendingDown } from "lucide-react";
